import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import Seo from '../common/Seo';
import { FaCalendarAlt, FaClock, FaArrowLeft, FaWhatsapp, FaFacebook, FaTwitter, FaLinkedin } from 'react-icons/fa';
import { BLOG_POSTS } from '../../data/blog';
import { COMPANY_INFO } from '../../constants';
import ShareButton from '../common/ShareButton';

// Importar imágenes
import cuidadorasImg from '../../assets/cuidadoras.jpg';
import auxiliarImg from '../../assets/auxiliar.jpg';
import generalImg from '../../assets/general.jpg';

const imageMap = {
  cuidadoras: cuidadorasImg,
  auxiliar: auxiliarImg,
  general: generalImg
};

const BlogPost = () => {
  const { slug } = useParams();
  const post = BLOG_POSTS.find(p => p.slug === slug);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const image = imageMap[post.image] || generalImg;
  const shareUrl = `https://geriatriaserena.com/blog/${slug}`;
  const shareText = encodeURIComponent(post.title);

  // Separamos el primer párrafo (que responde la intención de búsqueda) del resto
  // para insertar ahí mismo el resumen TL;DR y el CTA, antes de continuar con el artículo.
  const firstParagraphEnd = post.content.indexOf('</p>');
  const introHtml = firstParagraphEnd !== -1 ? post.content.slice(0, firstParagraphEnd + 4) : post.content;
  const restHtml = firstParagraphEnd !== -1 ? post.content.slice(firstParagraphEnd + 4) : '';

  // Obtener posts relacionados
  const relatedPosts = BLOG_POSTS
    .filter(p => p.id !== post.id && p.category === post.category)
    .slice(0, 3);

  const handleWhatsAppShare = () => {
    window.open(
      `https://wa.me/?text=${encodeURIComponent(post.title + ' - ' + shareUrl)}`,
      '_blank'
    );
  };

  return (
    <>
      <Seo
        title={`${post.title} | ${COMPANY_INFO.name}`}
        description={post.excerpt}
        keywords={post.tags.join(', ')}
        canonical={shareUrl}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": post.title,
          "description": post.excerpt,
          "author": {
            "@type": "Organization",
            "name": COMPANY_INFO.name
          },
          "publisher": {
            "@type": "Organization",
            "name": COMPANY_INFO.name
          },
          "datePublished": post.date,
          "dateModified": post.date
        }}
        meta={[
          { property: 'og:title', content: post.title },
          { property: 'og:description', content: post.excerpt },
          { property: 'og:type', content: 'article' },
          { property: 'og:url', content: shareUrl },
          { name: 'twitter:card', content: 'summary_large_image' },
          { name: 'twitter:title', content: post.title },
          { name: 'twitter:description', content: post.excerpt }
        ]}
      />

      <main className="min-h-screen bg-gray-50">
        {/* Hero */}
        <section className="relative h-[400px] md:h-[500px]">
          <img
            src={image}
            alt={post.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
          
          <div className="absolute bottom-0 left-0 right-0 p-8">
            <div className="max-w-4xl mx-auto">
              <Link 
                to="/blog"
                className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
              >
                <FaArrowLeft /> Volver al blog
              </Link>
              
              <span className="inline-block bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-medium mb-4">
                {post.category}
              </span>
              
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
                {post.title}
              </h1>
              
              <div className="flex flex-wrap items-center gap-6 text-white/80">
                <span className="flex items-center gap-2">
                  <FaCalendarAlt />
                  {new Date(post.date).toLocaleDateString('es-MX', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
                <span className="flex items-center gap-2">
                  <FaClock />
                  {post.readTime} de lectura
                </span>
                <span>Por {post.author}</span>
                <ShareButton
                  title={post.title}
                  text={post.excerpt}
                  url={shareUrl}
                  className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              {/* Article Content */}
              <article className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-600 prose-li:text-gray-600 prose-a:text-blue-600">
                <div dangerouslySetInnerHTML={{ __html: introHtml }} />

                {post.keyPoints && post.keyPoints.length > 0 && (
                  <div className="not-prose bg-blue-50 border border-blue-100 rounded-xl p-6 my-6">
                    <h2 className="font-bold text-blue-900 mb-4 text-lg">
                      Resumen rápido: 5 puntos clave
                    </h2>
                    <ul className="space-y-2">
                      {post.keyPoints.map((point, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <span className="text-blue-600 font-bold mt-0.5">{index + 1}.</span>
                          <span className="text-blue-900">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="not-prose flex flex-col sm:flex-row gap-3 mb-8">
                  <a
                    href={`https://wa.me/${COMPANY_INFO.whatsapp}?text=${encodeURIComponent(`Hola, leí el artículo "${post.title}" y me gustaría recibir asesoría.`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-bold transition-colors"
                  >
                    <FaWhatsapp /> Pedir asesoría sobre este tema
                  </a>
                </div>

                <div dangerouslySetInnerHTML={{ __html: restHtml }} />
              </article>

              {/* Tags */}
              <div className="mt-12 pt-8 border-t">
                <h3 className="font-bold text-gray-900 mb-4">Etiquetas:</h3>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag, index) => (
                    <span 
                      key={index}
                      className="bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-sm"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Share */}
              <div className="mt-8 pt-8 border-t">
                <h3 className="font-bold text-gray-900 mb-4">Compartir artículo:</h3>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={handleWhatsAppShare}
                    className="flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <FaWhatsapp /> WhatsApp
                  </button>
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaFacebook /> Facebook
                  </a>
                  <a
                    href={`https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-sky-500 text-white px-4 py-2 rounded-lg hover:bg-sky-600 transition-colors"
                  >
                    <FaTwitter /> Twitter
                  </a>
                  <a
                    href={`https://www.linkedin.com/shareArticle?mini=true&url=${shareUrl}&title=${shareText}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors"
                  >
                    <FaLinkedin /> LinkedIn
                  </a>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-12 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 text-white text-center">
              <h3 className="text-2xl font-bold mb-4">
                ¿Necesitas ayuda profesional?
              </h3>
              <p className="text-blue-100 mb-6">
                Contáctanos para recibir asesoría sobre el cuidado de tu familiar
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href={`https://wa.me/${COMPANY_INFO.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-bold flex items-center justify-center gap-2"
                >
                  <FaWhatsapp /> Contactar por WhatsApp
                </a>
                {post.relatedService ? (
                  <Link
                    to={post.relatedService.path}
                    className="bg-white text-blue-600 px-6 py-3 rounded-lg font-bold hover:bg-gray-100"
                  >
                    Ver {post.relatedService.label}
                  </Link>
                ) : (
                  <Link
                    to="/contact"
                    className="bg-white text-blue-600 px-6 py-3 rounded-lg font-bold hover:bg-gray-100"
                  >
                    Ver más información
                  </Link>
                )}
              </div>
            </div>

            {/* Related Posts */}
            {relatedPosts.length > 0 && (
              <div className="mt-12">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Artículos relacionados
                </h3>
                <div className="grid md:grid-cols-3 gap-6">
                  {relatedPosts.map(relatedPost => (
                    <Link
                      key={relatedPost.id}
                      to={`/blog/${relatedPost.slug}`}
                      className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow group"
                    >
                      <img
                        src={imageMap[relatedPost.image] || generalImg}
                        alt={relatedPost.title}
                        className="w-full h-32 object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="p-4">
                        <h4 className="font-bold text-gray-900 line-clamp-2 group-hover:text-blue-600 transition-colors">
                          {relatedPost.title}
                        </h4>
                        <p className="text-sm text-gray-500 mt-2">
                          {relatedPost.readTime}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
};

export default BlogPost;
